import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {GridIdentifier} from '@application/grids/grid-identifier.enum';
import {CrudActions} from '@application/helper/grid-action-buttons-helper/crud-actions.interface';
import {GridActionButtonsHelper} from '@application/helper/grid-action-buttons-helper/grid-action-buttons-helper';
import {RouteUtils} from '@application/helper/routing/route-utils';
import {PlasticProduct} from '@domain/textile-data/plastic-product/plastic-product';
import {PlasticProductGridType} from '@presentation/pages/textile-data/plastic-product/overview/plastic-product-grid-type.enum';
import {PlasticProductOverviewService} from '@presentation/pages/textile-data/plastic-product/overview/plastic-product-overview.service';
import {PlasticProductSelectionService} from '@presentation/pages/textile-data/plastic-product/plastic-product-selection.service';
import {
  AlertDialogResult,
  AssertionUtils,
  BaseComponent,
  CrudOverviewDataBuilderFactoryService,
  CrudOverviewDataConfig,
  DialogBuilderFactoryService,
  DialogType,
  GridButtonClickedEvent,
  GridOptionsBuilder,
  NoDataOverlayComponentParams,
  OverlayActionsService,
  OverlayComponentParams,
  OverlayComponentParamsAction,
  TranslateService
} from '@vdw/angular-component-library';
import {GetRowIdParams, GridApi, GridReadyEvent} from 'ag-grid-community';
import {filter, takeUntil} from 'rxjs';
import {AddToolNavigationData} from '../add-tool-navigation-data.interface';
import {AddToolPageComponent} from '../add-tool-page.component';

@Component({
  selector: 'app-products',
  templateUrl: './products.component.html'
})
export class ProductsComponent extends BaseComponent implements OnInit, OnChanges {
  @Output() public productsChange = new EventEmitter<PlasticProduct[]>();

  public isLoading = true;
  public config: CrudOverviewDataConfig<PlasticProduct>;
  public readonly TRANSLATION_KEY = 'UTILITIES.TOOL.PRODUCTS.PRODUCT';

  private readonly noOverlayAction: OverlayComponentParamsAction[] = [
    {
      key: 'addProduct',
      titleKey: this.translate.instant('GENERAL.ACTIONS.ADD_OBJECT', {object: this.translate.instant(this.TRANSLATION_KEY, {count: 2}).toLowerCase()}),
      isPrimary: true
    }
  ];

  private configGridApi: GridApi;
  private selectedProducts: PlasticProduct[] = [];

  public constructor(
    private readonly overlayActionsService: OverlayActionsService,
    private readonly translate: TranslateService,
    private readonly dialogBuilderFactoryService: DialogBuilderFactoryService,
    private readonly gridActionButtonsHelper: GridActionButtonsHelper,
    private readonly builderFactoryService: CrudOverviewDataBuilderFactoryService<PlasticProduct>,
    private readonly plasticProductOverviewService: PlasticProductOverviewService,
    private readonly plasticProductSelectionService: PlasticProductSelectionService,
    private readonly addPage: AddToolPageComponent
  ) {
    super();
    this.plasticProductSelectionService.init(true, (id: number) => this.selectedProducts.find((product: PlasticProduct) => product.id === id)?.isLocked);
  }

  @Input() public set products(selectedProducts: PlasticProduct[]) {
    this.isLoading = false;
    this.configGridApi?.setGridOption('loading', false);

    this.selectedProducts = selectedProducts;
  }

  public ngOnInit(): void {
    this.subscribeToOverlayActions();
    this.setConfig();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('products' in changes && !changes.products.isFirstChange()) {
      this.setRowData(changes.products.currentValue);
      this.configGridApi.redrawRows();
    }
  }

  public selectProducts(): void {
    this.plasticProductSelectionService
      .openContentSwitcherDialog(this.selectedProducts, {activeTab: this.addPage.selectedMenuItem, tool: this.addPage.getCurrentTool(), products: this.selectedProducts} as AddToolNavigationData)
      .subscribe((products: PlasticProduct[]) => {
        if (!AssertionUtils.isNullOrUndefined(products)) {
          this.productsChange.emit([...this.selectedProducts.filter((product: PlasticProduct) => product.isLocked), ...products]);
        }
      });
  }

  private subscribeToOverlayActions(): void {
    this.overlayActionsService.actionTriggeredEmitter.pipe(takeUntil(this.unSubscribeOnViewDestroy)).subscribe((actionKey: string) => {
      if (actionKey === this.noOverlayAction[0].key) {
        this.selectProducts();
      }
    });
  }

  private setConfig(): void {
    const builderFactory = this.builderFactoryService.getBuilder();

    builderFactory
      .withDefaultButtons({
        crudOverviewButtonConfigs: this.gridActionButtonsHelper.getActionButtonsForGridSelection({
          requiredPermissionToEdit: RouteUtils.paths.utilities.tool.editTool.requiredPermission,
          withDelete: (grid: GridButtonClickedEvent) => this.delete(grid),
          allowDeleteMultiple: true
        } as CrudActions)
      })
      .withCrudGridOptions(this.plasticProductOverviewService.getColumnDefs(true, PlasticProductGridType.INLINE), GridIdentifier.TOOL_PRODUCTS, null, (builder: GridOptionsBuilder) =>
        builder
          .withOnGridReady((params: GridReadyEvent) => {
            this.configGridApi = params.api;
            if (this.isLoading) {
              params.api.setGridOption('loading', true);
            } else {
              this.setRowData();
            }
          })
          .withOnFirstDataRendered(() => this.configGridApi.setGridOption('loading', false))
          .withRowSelection(true)
          .withGetRowId((params: GetRowIdParams) => params.data.name)
          .withLockUsedItem((data: PlasticProduct) => data.isLocked)
          .withPopupParent(document.querySelector('.mat-mdc-dialog-container'))
          .withLoadingOverlay({
            scale: 0.7
          } as OverlayComponentParams)
          .withNoRowsOverlay({
            scale: 1,
            titleParam: this.translate.instant(this.TRANSLATION_KEY, {count: 2}),
            hideDescription: false,
            actions: this.noOverlayAction
          } as NoDataOverlayComponentParams)
          .build()
      );

    this.config = builderFactory.build();
  }

  private delete({gridApi, rowNode}: GridButtonClickedEvent): void {
    this.dialogBuilderFactoryService
      .getBuilder()
      .openAlertDialog({
        titleText: this.translate.instant('GENERAL.ACTIONS.DELETE_OBJECT', {object: this.translate.instant(this.TRANSLATION_KEY, {count: 1}).toLowerCase()}),
        messageText: this.translate.instant('GENERAL.ACTIONS.CONFIRM_DELETE_DESCRIPTION'),
        type: DialogType.CONFIRM_DELETE
      })
      .pipe(
        filter((result: AlertDialogResult) => result === AlertDialogResult.CONFIRM),
        takeUntil(this.unSubscribeOnViewDestroy)
      )
      .subscribe(() => {
        const rowsToRemove = AssertionUtils.isNullOrUndefined(rowNode) ? gridApi?.getSelectedRows() : [rowNode.data];
        this.configGridApi.applyTransaction({remove: rowsToRemove});

        this.productsChange.emit(this.selectedProducts.filter((product: PlasticProduct) => !rowsToRemove.includes(product)));
      });
  }

  private setRowData(products: PlasticProduct[] = this.selectedProducts): void {
    this.configGridApi?.setGridOption('rowData', products);
    this.config.rowData = products;
  }
}
